import React, { useRef } from "react";
import { getShortAddress } from "../utils/address";
import Gap from "./Gap";

export interface StoreComparisonItem {
  id: string;
  name: string;
  address: string;
  score?: number; // 비교 가게의 점수 (AB 테스트 완료된 가게만)
}

/** 길게 누르기(미리보기) 인식 시간 */
const HOLD_MS = 500;

/**
 * 상호명이 두 줄이 될 때 **마지막 띄어쓰기에서만** 끊기게 만든다.
 *
 * '카페 델라치노 꼬또네 성수점' →  '카페 델라치노 꼬또네'
 *                                 '성수점'
 *
 * 어떻게
 * ------
 * 마지막 띄어쓰기만 보통 공백으로 두고, 앞쪽 띄어쓰기는 전부 줄바꿈이 안 되는
 * 공백(U+00A0)으로 바꾼다. 그러면 브라우저가 줄을 끊을 수 있는 자리가 마지막
 * 한 곳뿐이다. 보기에는 똑같은 공백이다.
 *
 * 글자 폭을 재지 않아도 되는 이유
 * -----------------------------
 * 줄바꿈은 브라우저가 '한 줄에 안 들어갈 때만' 한다. 그래서 '모찌 성수점'처럼
 * 한 줄에 들어가는 이름은 그대로 한 줄로 남고, 넘칠 때만 그 한 자리에서 끊긴다.
 * 길이에 따라 다르게 동작하지만 우리가 길이를 잴 필요는 없다.
 *
 * 앞부분이 그 자체로 한 줄을 넘으면(띄어쓰기 없는 아주 긴 이름) CSS의
 * overflow-wrap: anywhere가 받아서 글자 중간에서 끊는다 — 카드 밖으로
 * 삐져나가지는 않는다.
 */
const NBSP = '\u00A0'

const breakAtLastSpace = (name: string): string => {
  const trimmed = name.trim();
  const i = trimmed.lastIndexOf(" ");
  if (i === -1) return trimmed;
  // 앞쪽 공백은 안 끊기게 바꾸고, 마지막 하나만 보통 공백으로 남긴다
  return trimmed.slice(0, i).replace(/ /g, NBSP) + " " + trimmed.slice(i + 1);
};

/**
 * 비교 카드. onHoldStart/onHoldEnd가 주어지면(=B 카드) 길게 누르는 동안 미리보기 트리거.
 * 모듈 최상위 컴포넌트로 둬서 부모 재렌더 시 리마운트되지 않게(홀드 제스처가 끊기지 않게) 한다.
 */
const ComparisonCard: React.FC<{
  store: StoreComparisonItem;
  onClick: () => void;
  onHoldStart?: () => void;
  onHoldEnd?: () => void;
}> = ({ store, onClick, onHoldStart, onHoldEnd }) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldRef = useRef(false);
  const lastHoldEndRef = useRef(0);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const startHold = () => {
    if (!onHoldStart) return;
    heldRef.current = false;
    clearTimer();
    timerRef.current = setTimeout(() => {
      heldRef.current = true;
      onHoldStart();
    }, HOLD_MS);
  };

  const endHold = () => {
    clearTimer();
    if (heldRef.current) {
      heldRef.current = false;
      lastHoldEndRef.current = Date.now(); // 길게 누른 직후의 click(선택)은 잠시 무시
      onHoldEnd?.();
    }
  };

  const holdable = !!onHoldStart;

  return (
    <button
      type="button"
      onClick={() => {
        // 길게 누른 직후(400ms 내)의 click은 선택으로 처리하지 않음
        if (Date.now() - lastHoldEndRef.current < 400) {
          return;
        }
        onClick();
      }}
      onPointerDown={holdable ? startHold : undefined}
      onPointerUp={holdable ? endHold : undefined}
      onPointerLeave={holdable ? endHold : undefined}
      onPointerCancel={holdable ? endHold : undefined}
      onContextMenu={holdable ? (e) => e.preventDefault() : undefined}
      className="flex-1 rounded-16px px-20px py-16px flex flex-col justify-center transition-colors duration-200 hover:bg-gray-50 shadow-shadow2 border border-4B3621-20% overflow-hidden"
      style={{
        boxShadow: "0px 4px 10px 0px rgba(117, 76, 0, 0.1)",
        backgroundColor: "#ffffff",
        minHeight: "130px",
        WebkitTouchCallout: "none",
        userSelect: "none",
        touchAction: "manipulation",
      }}
    >
      <div
        className="w-full font-pretendard font-700 text-16px leading-125% tracking-[-3%] text-brown-80%-main text-center text-ellipsis-2"
        style={{
          // 한글 낱말 안에서는 끊지 않는다 → breakAtLastSpace가 남겨둔
          // 마지막 띄어쓰기 한 곳에서만 줄이 바뀐다.
          wordBreak: "keep-all",
          // 그 앞부분이 그 자체로 한 줄을 넘으면(띄어쓰기 없는 긴 이름)
          // 그때만 글자 중간에서 끊는다. 카드 밖으로 넘치지 않게 하는 안전장치.
          overflowWrap: "anywhere",
        }}
      >
        {breakAtLastSpace(store.name)}
      </div>
      <Gap height={8} />
      <div className="w-full font-pretendard font-regular text-14px leading-20px tracking-[-3%] text-textColorText3 text-center text-ellipsis-2 break-all">
        {getShortAddress(store.address)}
      </div>
    </button>
  );
};

/**
 * 뒤로가기 / 건너뛰기 아이콘.
 *
 * 두 아이콘은 같은 패스 하나를 쓰고, 건너뛰기만 scaleX(-1)로 뒤집는다.
 * 따로 그린 두 패스는 아무리 맞춰도 정확한 거울상이 되지 않는다.
 *
 * viewBox를 패스 실제 범위(x 4~20, y 4~21)에 선 두께 절반(1)을 더한 값으로
 * 잘랐다. 24×24 기본 박스를 그대로 쓰면 안쪽 여백 때문에 아이콘이 좌우
 * 20px 선에서 3px쯤 안으로 들어가 보인다.
 */
const UndoArrow: React.FC<{ mirrored?: boolean }> = ({ mirrored = false }) => (
  <svg
    width={16}
    height={17}
    viewBox="3 3 18 19"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.1}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      display: "block",
      flexShrink: 0,
      transform: mirrored ? "scaleX(-1)" : undefined,
    }}
    aria-hidden
  >
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
  </svg>
);

/**
 * 하단 액션 버튼. 아이콘은 둘 다 글자 앞에 온다.
 *
 * 좌우 여백은 부모(.mt-20px px-20px)가 이미 20px이라 여기서 padding을 더 주지
 * 않는다. 세로 padding만 줘서 손가락이 닿을 넓이를 확보한다.
 *
 * 할 수 없을 때도 모양은 그대로 두고 반응만 없앤다. 첫 판에서는 되돌릴 게
 * 없는 게 당연한데, 그때마다 흐려지면 무언가 잘못된 것처럼 보인다.
 */
const ComparisonAction: React.FC<{
  label: string;
  mirrored?: boolean;
  disabled?: boolean;
  onClick: () => void;
}> = ({ label, mirrored, disabled, onClick }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    className="inline-flex items-center gap-6px py-6px font-pretendard font-600 text-15px text-textColorText3"
    style={{ lineHeight: 1, touchAction: "manipulation" }}
  >
    <UndoArrow mirrored={mirrored} />
    {label}
  </button>
);

interface StoreComparisonProps {
  store1: StoreComparisonItem;
  store2: StoreComparisonItem;
  selectedStoreId?: string | null;
  onSelect: (storeId: string) => void;
  /** store2(B)를 길게 누르는 동안 내 리뷰 미리보기 표시/숨김 */
  onPeekStart?: () => void;
  onPeekEnd?: () => void;
  /** VS 카드 영역 위에 얹을 오버레이(온보딩 손가락 힌트 등). 기본 미사용. */
  overlay?: React.ReactNode;
  /**
   * 뒤로가기·건너뛰기. 둘 다 주어질 때만 하단 액션 줄이 나온다
   * (온보딩 화면은 이 컴포넌트를 그대로 쓰되 액션은 필요 없다).
   */
  onUndo?: () => void;
  onSkip?: () => void;
  /** 되돌릴 판이 없으면 false — 버튼은 남고 흐려진다 */
  canUndo?: boolean;
  /** 이 구간에서 더 뽑을 상대가 없으면 false */
  canSkip?: boolean;
}

const StoreComparison: React.FC<StoreComparisonProps> = ({
  store1,
  store2,
  onSelect,
  onPeekStart,
  onPeekEnd,
  overlay,
  onUndo,
  onSkip,
  canUndo = false,
  canSkip = false,
}) => {
  return (
    <div className="mt-20px px-20px w-full flex flex-col items-start">
      <p className="font-pretendard font-bold text-16px leading-125% text-brown-80%-main text-left">
        어디가 더 좋았어요?
      </p>
      <p className="mt-6px font-pretendard font-regular text-14px leading-125% text-textColorText2 text-left">
        기억 안 나면 꾹 눌러보세요
      </p>

      {/* 부제 ↔ VS 카드 간격 20px (Figma) */}
      <div className="mt-20px w-full flex items-stretch gap-16px relative">
        {/* VS 아이콘 */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40px h-40px rounded-full bg-4B3621-80% flex items-center justify-center flex-shrink-0 z-10">
          <span className="font-pretendard font-700 text-14px leading-125% text-white text-center">
            VS
          </span>
        </div>

        {/* 왼쪽 카드 (A: 새 장소) */}
        <ComparisonCard store={store1} onClick={() => onSelect(store1.id)} />

        {/* 오른쪽 카드 (B: 비교 대상 — 길게 누르면 내 리뷰 미리보기) */}
        <ComparisonCard
          store={store2}
          onClick={() => onSelect(store2.id)}
          onHoldStart={onPeekStart}
          onHoldEnd={onPeekEnd}
        />
        {overlay}
      </div>

      {onUndo && onSkip && (
        <div className="mt-22px w-full flex items-center justify-between">
          <ComparisonAction
            label="뒤로가기"
            disabled={!canUndo}
            onClick={onUndo}
          />
          <ComparisonAction
            label="건너뛰기"
            mirrored
            disabled={!canSkip}
            onClick={onSkip}
          />
        </div>
      )}
    </div>
  );
};

export default StoreComparison;
